/**
 * useOAuthProviders — probes which social sign-in providers the backend has
 * configured.
 *
 * GET /api/auth/oauth/providers → { google: boolean, github: boolean }
 * (no auth). Both are false until the corresponding secrets are configured
 * server-side, so the UI must treat "absent" as "disabled".
 *
 * Probes the CLOUD via `authClient` (authUrl), NOT the sidecar — the sidecar
 * has no oauth/providers route, so probing it always failed closed and hid the
 * Google button even when the cloud had it configured (the Google-button bug).
 *
 * FAILS CLOSED: any network error, non-OK response, or malformed payload
 * resolves to `{ google: false, github: false }`. The OAuthButtons component
 * renders nothing for disabled providers, so a failed probe simply hides the
 * social buttons rather than showing dead controls.
 *
 * Per L:/GDB/.claude/rules/react-patterns.md: centralised query key via the
 * `authKeys` factory, useQuery (never useEffect for fetching). Provider
 * config rarely changes within a session, so a generous staleTime keeps the
 * login/register pages from re-probing on every mount.
 */

import { useQuery } from '@tanstack/react-query'
import { authClient } from '@/lib/api-client'
import { authKeys } from './authKeys'

export interface OAuthProviders {
  google: boolean
  github: boolean
}

const DISABLED: OAuthProviders = { google: false, github: false }

/**
 * Coerce an unknown payload into a strict OAuthProviders shape. Anything that
 * is not a literal boolean `true` becomes `false` — fail closed.
 */
function parseProviders(raw: unknown): OAuthProviders {
  if (typeof raw !== 'object' || raw === null) return DISABLED
  const obj = raw as Record<string, unknown>
  return {
    google: obj.google === true,
    github: obj.github === true,
  }
}

export interface UseOAuthProvidersResult {
  providers: OAuthProviders
  isLoading: boolean
}

export function useOAuthProviders(): UseOAuthProvidersResult {
  const { data, isLoading } = useQuery<OAuthProviders>({
    queryKey: authKeys.oauthProviders(),
    queryFn: async (): Promise<OAuthProviders> => {
      // Fail closed on any error — return DISABLED rather than throwing so
      // the buttons just don't render. A thrown error here would put the
      // query into an error state that callers would have to special-case.
      try {
        const response = await authClient.get('/api/auth/oauth/providers')
        if (!response.ok) return DISABLED
        const json: unknown = await response.json()
        return parseProviders(json)
      } catch {
        return DISABLED
      }
    },
    // Provider availability is effectively static config — probe once and
    // hold it for the session. 5 minutes is plenty; a hard reload re-probes.
    staleTime: 5 * 60_000,
    retry: false,
  })

  return {
    providers: data ?? DISABLED,
    isLoading,
  }
}
